import { Background, BackgroundColor, Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import React, { useEffect, useMemo, useState } from "react"
import { Repeater, types, useAdminContext, usePageValues } from "react-bricks/frontend"

import { EqualHeight } from "react-equal-height/clean"
import { MatrixItemProps } from "./matrix/Item"
import classNames from "classnames"
import { useLocalesState } from "../../shared/context/locales"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"

export interface ProductMatrixProps {
    repeater?: {
        id: string
        props: MatrixItemProps
        type: "MatrixItem"
    }[]
    sku?: string
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    id?: string
    index?: number
}

const ProductMatrix: types.Brick<ProductMatrixProps> = ({
    repeater,
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
    id,
    sku,
    index,
}) => {
    const { region } = useLocalesState()
    const { products: productsBuildTime, page: pageContext } = usePageContext()
    const client = useSDK()

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", ...(repeater?.map(r => r.props.sku).filter(r => r) || []), region, page.language],
        queryFn: ({ signal }) =>
            client.getProducts(
                { sku: repeater?.map(r => r.props.sku).filter((r): r is string => !!r), country: region },
                page.language,
                signal
            ),
        enabled: !!repeater?.length,
        meta: { background: true },
    })

    const products = data || productsBuildTime

    const [activeTab, setActiveTab] = useState(repeater?.findIndex(r => r.props.sku === sku) || 0)

    const changeTab = (sku?: string) => {
        if (!repeater || !sku) return
        setActiveTab(repeater.findIndex(r => r.props.sku === sku))
    }

    const { isAdmin } = useAdminContext()

    useEffect(() => {
        if (!products || !repeater) return

        setActiveTab(
            repeater.findIndex(
                r =>
                    !r.props.sku || (products.map(p => p.sku).includes(r.props.sku) && sku ? r.props.sku === sku : true)
            ) || 0
        )
    }, [products, repeater, sku])

    const repeaterLengthFiltered = useMemo(
        () =>
            repeater?.filter(r => !(r.props.sku && !products?.find(p => p.sku === r.props.sku) && !isAdmin)).length ||
            0,
        [repeater, products, isAdmin]
    )

    return (
        <div key={index}>
            <Background color={BackgroundColor.White} className="overflow-hidden">
                <Container>
                    <div id={id}>
                        <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
                        <Repeater propName="centerText" />
                        <EqualHeight updateOnChange={[repeaterLengthFiltered, activeTab]}>
                            <ul
                                className={classNames(
                                    "grid gap-1.5 pt-5 sm:gap-3.5 md:gap-5 lg:gap-6",
                                    repeaterLengthFiltered % 3 === 0
                                        ? "grid-cols-3 sm:grid-cols-6"
                                        : "grid-cols-[repeat(2,1fr)] sm:grid-cols-4 lg:grid-cols-6"
                                )}
                            >
                                <Repeater
                                    propName="repeater"
                                    itemProps={
                                        {
                                            products,
                                            repeaterLength: repeater?.length || 0,
                                            repeaterLengthFiltered,
                                            changeTab,
                                            activeTab,
                                            firstOnMobile: sku,
                                        } as MatrixItemProps
                                    }
                                />
                            </ul>
                            <ul className="sm:hidden">
                                <Repeater
                                    propName="repeater"
                                    itemProps={
                                        {
                                            products,
                                            repeaterLengthFiltered,
                                            changeTab,
                                            activeTab,
                                            showActiveOnly: true,
                                        } as MatrixItemProps
                                    }
                                />
                            </ul>
                        </EqualHeight>
                    </div>
                    <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
                </Container>
            </Background>
        </div>
    )
}

ProductMatrix.schema = {
    name: "ProductMatrix",
    label: "Product Matrix",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/ProductMatrix.png`,
    repeaterItems: [
        {
            name: "centerText",
            items: [
                {
                    type: "Trust",
                    label: "Trust Badge",
                    min: 0,
                    max: 1,
                },
                {
                    type: "CenterText",
                    label: "Text",
                    max: 1,
                },
            ],
        },
        {
            name: "repeater",
            items: [
                {
                    type: "MatrixItem",
                    label: "Product",
                    min: 2,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "first Product Mobile",
            type: types.SideEditPropType.Text,
        },
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): ProductMatrixProps => ({
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Small,
        variantBottom: SpacerVariant.Small,
    }),
}

export default ProductMatrix
